/**
 * API client · ECG.WATCH backend
 * ─────────────────────────────────────────────────────────────
 * Envuelve fetch() con:
 *   - base URL configurable por env (VITE_API_BASE_URL)
 *   - header X-Clinician-Id leído de localStorage
 *   - tipos TS alineados con las schemas de Pydantic del backend
 *   - errores clasificados (Unauthorized, NotFound, ServerError, Network)
 *
 * Endpoints cubiertos (Fase 1):
 *   GET  /api/v1/healthz                        → healthcheck
 *   GET  /api/v1/clinical/references            → catálogo de guías
 *   GET  /api/v1/patients                       → lista
 *   GET  /api/v1/patients/{id}                  → stats + timeline
 *   GET  /api/v1/patients/{id}/ecgs             → ECGs del paciente
 *   GET  /api/v1/ecgs/{id}                      → detalle ECG
 *   PATCH /api/v1/ecgs/{id}/notes               → actualizar nota
 *   POST /api/v1/ecg/upload                     → upload múltiple
 */

// ─── Config ────────────────────────────────────────────────────
const DEFAULT_API_BASE = "https://api.ecgwatch.online";

export function getApiBase(): string {
  // Prioriza variable de entorno (para staging/dev), cae a prod
  return import.meta.env.VITE_API_BASE_URL || DEFAULT_API_BASE;
}

// ─── Auth storage (placeholder hasta JWT real) ─────────────────
const STORAGE_KEY = "ecgwatch.clinicianId";

export function getClinicianId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setClinicianId(id: string): void {
  localStorage.setItem(STORAGE_KEY, id);
}

export function clearClinicianId(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── AuthV2 — JWT Bearer + clinician summary ────────────────────
// El access token vive solo en memoria (no en localStorage) para reducir
// superficie XSS. Persiste mientras la pestana este abierta; al refresh
// se recupera via cookie HttpOnly del refresh token (POST /auth/refresh).
// El clinician summary SI se persiste en localStorage para mostrar la UI
// sin esperar el primer /me. El X-Clinician-Id legacy se sigue escribiendo
// con clinician.id para compat con el resto del BE.

const ACCESS_TOKEN_KEY = "ecgwatch.authv2.accessToken";
const CLINICIAN_SUMMARY_KEY = "ecgwatch.authv2.clinicianSummary";

let _inMemoryAccessToken: string | null = null;

export interface AuthV2ClinicianSummary {
  id: string;
  full_name: string;
  email: string;
  // 'admin' agregado en AdminPanel v0.2 S2.0 — habilita gating de /admin/*
  role: "admin" | "advisor" | "practitioner" | "validator" | "viewer";
  specialty_primary?: string | null;
  preferred_home_route: string;
}

/** True si el clinician autenticado tiene role='admin'. Lee de localStorage. */
export function isCurrentUserAdmin(): boolean {
  return getClinicianSummary()?.role === "admin";
}

/**
 * True si el role puede consumir hallazgos DRAFT.
 *
 * Phase B · S1.2 Sesion 7 — PRD VisualAids §15. Mientras `premature_beats@v0.1`
 * este DRAFT (pendiente firma S1.3), solo admin/validator/advisor pueden ver
 * los hallazgos sobre el trazo. El backend duplica este check en el endpoint
 * /findings (403 si no privilegiado) y en /ecgs/{id} (oculta fields draft);
 * el FE lo usa para no llamar al endpoint cuando no toca.
 */
export function isCurrentUserPrivilegedForDrafts(): boolean {
  const role = getClinicianSummary()?.role;
  return role === "admin" || role === "validator" || role === "advisor";
}

export function getAccessToken(): string | null {
  if (_inMemoryAccessToken) return _inMemoryAccessToken;
  try {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  _inMemoryAccessToken = token;
  try {
    if (token) sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    else sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getClinicianSummary(): AuthV2ClinicianSummary | null {
  try {
    const raw = localStorage.getItem(CLINICIAN_SUMMARY_KEY);
    return raw ? (JSON.parse(raw) as AuthV2ClinicianSummary) : null;
  } catch {
    return null;
  }
}

export function setClinicianSummary(summary: AuthV2ClinicianSummary | null): void {
  try {
    if (summary) {
      localStorage.setItem(CLINICIAN_SUMMARY_KEY, JSON.stringify(summary));
      // Mantiene X-Clinician-Id sincronizado para BE legacy
      setClinicianId(summary.id);
    } else {
      localStorage.removeItem(CLINICIAN_SUMMARY_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearAuthV2Session(): void {
  setAccessToken(null);
  setClinicianSummary(null);
  clearClinicianId();
}

// ─── AuthV2 endpoints ───────────────────────────────────────────

interface LoginResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  clinician: AuthV2ClinicianSummary;
}

async function postAuthV2<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getApiBase()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include", // refresh cookie HttpOnly
  });
  if (!res.ok) {
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      /* ignore */
    }
    if (res.status === 401 || res.status === 403)
      throw new UnauthorizedError(parsed);
    throw new ApiError(res.status, parsed, `auth request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export async function authV2AppleCallback(idToken: string): Promise<LoginResponse> {
  const r = await postAuthV2<LoginResponse>("/auth/apple/callback", {
    id_token: idToken,
  });
  setAccessToken(r.access_token);
  setClinicianSummary(r.clinician);
  return r;
}

export async function authV2GoogleCallback(credential: string): Promise<LoginResponse> {
  const r = await postAuthV2<LoginResponse>("/auth/google/callback", {
    credential,
  });
  setAccessToken(r.access_token);
  setClinicianSummary(r.clinician);
  return r;
}

export async function authV2OtpRequest(email: string): Promise<void> {
  await postAuthV2<{ ok: boolean; message: string }>("/auth/otp/request", {
    email,
  });
}

export async function authV2OtpVerify(
  email: string,
  code: string,
): Promise<LoginResponse> {
  const r = await postAuthV2<LoginResponse>("/auth/otp/verify", { email, code });
  setAccessToken(r.access_token);
  setClinicianSummary(r.clinician);
  return r;
}

export async function authV2Refresh(): Promise<{ access_token: string } | null> {
  try {
    const res = await fetch(`${getApiBase()}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { access_token: string };
    setAccessToken(body.access_token);
    return body;
  } catch {
    return null;
  }
}

export async function authV2Logout(): Promise<void> {
  const token = getAccessToken();
  try {
    await fetch(`${getApiBase()}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  } catch {
    /* ignore */
  } finally {
    clearAuthV2Session();
  }
}

// ─── Errores tipados ───────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class UnauthorizedError extends ApiError {
  constructor(body: unknown) {
    super(401, body, "Credenciales inválidas");
    this.name = "UnauthorizedError";
  }
}

export class NotFoundError extends ApiError {
  constructor(body: unknown) {
    super(404, body, "Recurso no encontrado");
    this.name = "NotFoundError";
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Error de red — verificá conectividad o dominio del backend");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

// ─── Tipos del backend (espejo de Pydantic) ────────────────────

export type AppleClassification =
  | "sinus_rhythm"
  | "atrial_fibrillation"
  | "high_heart_rate"
  | "low_heart_rate"
  | "inconclusive_poor_reading"
  | "inconclusive_not_classified";

export type IngestOutcome = "created" | "duplicate" | "parse_error" | "validation_error";

export interface PatientOut {
  id: string;
  full_name: string;
  birth_date: string | null;
  sex: "F" | "M" | null;
  clinical_notes: string | null;
  is_demo: boolean;
  created_at: string;
  ecg_count: number;

  // PRD Dashboard v0.2 sec 4.5 - campos derivados del ultimo ECG y cohorte 30d.
  // Todos opcionales: pacientes sin ECGs tienen estos campos en null/0.
  last_ecg_date?: string | null;
  last_ecg_hr_bpm?: number | null;
  last_ecg_rhythm?: string | null;
  last_ecg_rhythm_label?: string | null;
  days_since_last_ecg?: number | null;
  ecgs_last_30d?: number;
}

/**
 * Payload del PATCH /api/v1/patients/{id} (S2.6).
 *
 * Todos los campos son opcionales: solo los provistos se actualizan.
 * Cada cambio queda registrado en audit_log del backend.
 */
export interface PatientUpdatePayload {
  full_name?: string;
  birth_date?: string;        // ISO date (YYYY-MM-DD)
  sex?: "F" | "M" | null;
  clinical_notes?: string | null;
  is_demo?: boolean;
}

export interface TimelinePoint {
  ecg_record_id: string;
  display_id: string;
  recorded_at: string;
  apple_classification: AppleClassification;
  average_heart_rate_bpm: number;
  measured_hr_mean_bpm: number | null;
  measured_hr_mean_version: string | null;
  measured_qtc_bazett_ms: number | null;
  measured_sdnn_ms: number | null;
  measured_sdnn_version: string | null;
  measured_pr_mean_ms: number | null;
  measured_pr_version: string | null;
  measured_qrs_mean_ms: number | null;
  measured_qrs_version: string | null;
  is_outlier_hr_mean: boolean | null;
  is_outlier_sdnn: boolean | null;
}

/** Baseline personal de una métrica: mean ± std sobre N ECGs sinusales. */
export interface BaselineMetric {
  mean: number;
  std: number;
  n: number;
}

/**
 * Baseline personal del paciente. Se activa al alcanzar `n_required` ECGs
 * en ritmo sinusal con la métrica medida disponible. Cada métrica tiene
 * baseline propio (puede haber baseline para HR y no para SDNN).
 */
export interface PatientBaseline {
  n_required: number;
  n_available_sinus: number;
  n_excluded_non_rest?: number;
  hr_mean: BaselineMetric | null;
  sdnn: BaselineMetric | null;
  // PRD PatientDetail v0.5 chip strip (B1)
  qtc_bazett: BaselineMetric | null;
  qtc_fridericia: BaselineMetric | null;
  pr: BaselineMetric | null;
  qrs: BaselineMetric | null;
  /** Ventana en dias aplicada al baseline. null = all-history. */
  window_days: number | null;
}

export interface PatientAggregateStats {
  patient_id: string;
  ecg_count: number;
  date_range_start: string | null;
  date_range_end: string | null;
  avg_heart_rate_bpm: number | null;
  min_heart_rate_bpm: number | null;
  max_heart_rate_bpm: number | null;
  classification_distribution: Record<string, number>;
  /** PRD PatientDetail v0.5 source pills: {healthkit: 81, pdf: 5, unknown: 0}. */
  data_source_distribution?: Record<string, number>;
  timeline: TimelinePoint[];
  afib_burden_pct: number;
  baseline: PatientBaseline;
}

export type PolarityStatus = "normal" | "inverted_suspected" | "undetermined";

export interface ECGRecordSummary {
  id: string;
  display_id: string;
  patient_id: string;
  recorded_at: string;
  apple_classification: AppleClassification;
  average_heart_rate_bpm: number;
  has_clinician_notes: boolean;
  created_at: string;
  /** Bloque B (3-jul-2026): ECGs con 'inverted_suspected' se excluyen de
   *  presets del wizard. null = polarity@v0.1 no corrió (HealthKit). */
  measured_polarity_status: PolarityStatus | null;
}

/** Parámetros técnicos del Apple Watch (extraídos del PDF). */
export interface TechnicalParameters {
  paper_speed_mm_per_s: number;
  amplitude_mm_per_mv: number;
  lead: string;
  sample_rate_hz: number;
  ios_version: string | null;
  watchos_version: string | null;
  watch_model: string | null;
  algorithm_version: string | null;
}

export interface ECGRecordDetail extends ECGRecordSummary {
  duration_seconds: number;
  apple_classification_description_es: string | null;
  user_reported_symptoms: string[];
  technical_parameters: TechnicalParameters;
  source_filename: string | null;
  source_file_sha256: string;
  parser_version: string;
  parsed_at: string;
  clinician_notes: string | null;
  clinician_notes_updated_at: string | null;
  /** @deprecated legacy; usar measured_qt_mean_ms */
  measured_qt_ms: number | null;
  measured_qtc_bazett_ms: number | null;
  measurement_error_estimate_ms: number | null;
  measurement_method: string | null;
  /** qt@v1.0 — promedio del trazado en ms. */
  measured_qt_mean_ms: number | null;
  /** Standard Error del promedio: SD/sqrt(N). */
  measured_qt_se_ms: number | null;
  /** QT maximo del registro — riesgo torsade. */
  measured_qt_max_ms: number | null;
  /** Cantidad de QT validos usados para el promedio. */
  measured_qt_n_beats: number | null;
  measured_qt_version: string | null;
  measured_qt_at: string | null;
  /** qtc Bazett — maximo del registro. */
  measured_qtc_bazett_max_ms: number | null;
  measured_qtc_bazett_version: string | null;
  measured_qtc_bazett_at: string | null;
  /** qtc Fridericia — mediano y maximo. */
  measured_qtc_fridericia_ms: number | null;
  measured_qtc_fridericia_max_ms: number | null;
  measured_qtc_fridericia_version: string | null;
  measured_qtc_fridericia_at: string | null;
  measured_hr_mean_bpm: number | null;
  measured_hr_mean_version: string | null;
  measured_hr_mean_at: string | null;
  measured_sdnn_ms: number | null;
  measured_sdnn_version: string | null;
  measured_sdnn_at: string | null;
  /** pr@v1.1 — promedio del trazado en ms (NO latido-a-latido, ver L8). */
  measured_pr_mean_ms: number | null;
  /** Standard Error del promedio: SD/√N. */
  measured_pr_se_ms: number | null;
  /** Cantidad de PR válidos usados para calcular el promedio. */
  measured_pr_n_beats: number | null;
  measured_pr_version: string | null;
  measured_pr_at: string | null;
  /** qrs@v1.0 — promedio del trazado en ms (NO latido-a-latido, ver L8). */
  measured_qrs_mean_ms: number | null;
  /** Standard Error del promedio: SD/√N. */
  measured_qrs_se_ms: number | null;
  /** Cantidad de QRS válidos usados para calcular el promedio. */
  measured_qrs_n_beats: number | null;
  measured_qrs_version: string | null;
  measured_qrs_at: string | null;
  /**
   * signal_quality@v0.1 (Phase A bis). Pendiente firma de Dr. Raul (Sesion 4);
   * sube a v1.0 post-firma. score_0_100 NO se expone (uso interno per spec MVP).
   */
  measured_signal_quality_snr_db: number | null;
  measured_signal_quality_baseline_mv: number | null;
  measured_signal_quality_emg_ratio: number | null;
  measured_signal_quality_saturation_pct: number | null;
  measured_signal_quality_rpeak_cv: number | null;
  measured_signal_quality_twave_clarity: number | null;
  measured_signal_quality_category: "alta" | "media" | "baja" | null;
  measured_signal_quality_version: string | null;
  measured_signal_quality_at: string | null;
  signal_reconstructed: boolean;
  /**
   * signal_quality gate (Phase A bis, Sesion 5). True cuando el flag
   * SIGNAL_QUALITY_GATE_ENABLED esta ON en backend y este ECG tiene
   * measured_signal_quality_category === "baja". Cuando es true, los
   * 5 measured_<metric>_* (hr/sdnn/pr/qrs/qt/qtc) vienen como null en
   * la response. El flag arranca en false hasta firma de v0.2.
   *
   * NOTA (backlog P0 #4, incremento frontend): este flag quedo dormido
   * en produccion (el gate nunca se activo) y ningun componente lo
   * necesita mas — metrics_quality_status ya cubre este caso exacto
   * (value=null + quality_category="baja" -> "insufficient_quality"),
   * de forma per-metrica en vez de un booleano global. Se deja el campo
   * en el tipo por si algun consumidor externo todavia lo lee, pero los
   * bloques de metricas (Hr/Sdnn/Qrs/Pr/QtcBlock) ya no lo consultan.
   */
  gated_by_signal_quality: boolean;
  /**
   * Backlog P0 #4: status explicito por metrica (measured/
   * insufficient_quality/not_run/not_computed) para las 7 metricas
   * gateadas por calidad de señal. Ver metrics_quality_reason para el
   * texto explicativo listo para UI. El backend ya lo expone hace rato
   * (ver ECGRecordDetail en schemas.py) — este incremento es solo el
   * consumo frontend.
   */
  metrics_quality_status: Record<MetricKey, MetricQualityStatus>;
  /** Texto en español, compañero de metrics_quality_status. */
  metrics_quality_reason: Record<MetricKey, string>;
  /**
   * Backlog P0 #4 (segunda parte): por qué GET /ecgs/{id}/findings vino
   * vacío — "measured" (el array puede tener 0 o más hallazgos reales) o
   * "not_run" (el algoritmo nunca corrió sobre este registro, o corrió y
   * no pudo con esta señal). Ver premature_beats_reason para el texto.
   */
  premature_beats_status: "measured" | "not_run";
  /** Texto en español, compañero de premature_beats_status. */
  premature_beats_reason: string;
}

/** Las 7 métricas gateadas por calidad de señal (ver _METRIC_QUALITY_SPECS en schemas.py). */
export type MetricKey =
  | "hr_mean"
  | "sdnn"
  | "pr"
  | "qrs"
  | "qt"
  | "qtc_bazett"
  | "qtc_fridericia";

export type MetricQualityStatus =
  | "measured"
  | "insufficient_quality"
  | "not_run"
  | "not_computed";

export interface MethodologyPayload {
  metric_name: string;
  current_version: string;
  evidence_level: number | null;
  algorithm: {
    description: string;
    code_commit: string | null;
    libraries: Record<string, unknown>;
  };
  validation_summary: {
    results: Record<string, unknown>;
    approved_at: string | null;
    approved_by: string | null;
    decision: string | null;
  };
  limitations: string[];
  reference_doc: string | null;
  status: string;
}

export interface UploadResultItem {
  filename: string;
  outcome: IngestOutcome;
  ecg_record_id: string | null;
  ecg_display_id: string | null;
  patient_id: string | null;
  patient_name: string | null;
  recorded_at: string | null;
  apple_classification: AppleClassification | null;
  average_heart_rate_bpm: number | null;
  error_message: string | null;
}

export interface UploadResponse {
  total_files: number;
  created: number;
  duplicates: number;
  errors: number;
  results: UploadResultItem[];
}

export interface ClinicalReference {
  id: string;
  title: string;
  authors: string;
  journal: string;
  year: number;
  doi: string | null;
  url: string | null;
  publisher: string;
  applies_to: string[];
  notes: string;
}

export interface ClinicalReferencesResponse {
  count: number;
  references: ClinicalReference[];
  disclaimer_es: string;
}

export interface HealthResponse {
  status: "ok" | "degraded";
  version: string;
  environment: string;
  database: string;
}

export interface EcgSignalResponse {
  sample_rate_hz: number;
  duration_seconds: number;
  samples_mv: number[];
}

/**
 * Hallazgo algoritmico DRAFT sobre un ECG (Phase B VisualAids).
 * Mapea 1:1 con `ECGFindingResponse` Pydantic del backend.
 */
export interface ECGFindingResponse {
  id: string;
  kind: "premature_beat";
  start_seconds: number;
  end_seconds: number;
  params: Record<string, number | string>;
  severity: "low" | "moderate" | "high" | "critical";
  is_new: boolean;
  sens: number;
  esp: number;
  dataset: string;
  algorithm: string;
  commit_sha: string;
}

// ─── Waitlist (acceso anticipado, endpoint público) ────────────

export interface WaitlistSignupRequest {
  email: string;
  role: 'cardio' | 'patient';
  extra?: string | null;
  accepted_communications: boolean;
}

export interface WaitlistSignupResponse {
  id: string;
  submitted_at: string;
  is_new: boolean;
}

// ─── Colleague feedback (cuestionario público) ────────────────
export type Specialty =
  | "cardiologia"
  | "electrofisiologia"
  | "clinica"
  | "interna"
  | "neurologia"
  | "endocrinologia"
  | "psiquiatria"
  | "geriatria"
  | "deporte"
  | "anestesiologia"
  | "otra";

export type SurveyLevel = "express" | "standard" | "deep";

export interface ColleagueFeedbackRequest {
  full_name: string;
  email: string;
  specialty: Specialty;
  specialty_other: string | null;
  institution: string | null;
  invitation_code: string | null;
  survey_level: SurveyLevel;
  answers: Record<string, unknown>;
  wants_followup: boolean;
  accepted_communications: boolean;
}

export interface ColleagueFeedbackSubmitResponse {
  id: string;
  submitted_at: string;
  answered_count: number;
}

// ─── Fetch wrapper ─────────────────────────────────────────────
interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: BodyInit | object | null;
  signal?: AbortSignal;
  /** Si false, no agrega X-Clinician-Id (para endpoints públicos) */
  authenticated?: boolean;
}

// ─── Single-flight refresh helper ──────────────────────────────
// Duplicado de adminApi.ts. Si varios requests caen en 401 a la vez,
// comparten UN solo POST /auth/refresh en vez de disparar N.
// authV2Refresh() setea el access token nuevo y devuelve null si falla.
let inflightRefresh: Promise<boolean> | null = null;
function refreshAccessTokenOnce(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = authV2Refresh()
      .then((r) => r !== null)
      .catch(() => false)
      .finally(() => {
        inflightRefresh = null;
      });
  }
  return inflightRefresh;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, authenticated = true } = opts;

  const isFormData = body instanceof FormData;
  const url = `${getApiBase()}${path}`;

  // Helper: ejecuta el fetch con un set de headers de auth dado.
  const doFetch = async (
    authHeaders: Record<string, string>,
  ): Promise<Response> => {
    const headers: Record<string, string> = { ...authHeaders };
    if (body && !isFormData) {
      headers["Content-Type"] = "application/json";
    }
    try {
      return await fetch(url, {
        method,
        headers,
        body: body
          ? isFormData
            ? (body as FormData)
            : JSON.stringify(body)
          : undefined,
        signal,
      });
    } catch (e) {
      throw new NetworkError(e);
    }
  };

  // Helper: parsea response, hace throws apropiados, devuelve T.
  const parseAndReturn = async (response: Response): Promise<T> => {
    const text = await response.text();
    let parsed: unknown = text;
    if (text && response.headers.get("content-type")?.includes("application/json")) {
      try {
        parsed = JSON.parse(text);
      } catch {
        /* queda como string */
      }
    }
    if (!response.ok) {
      if (response.status === 401) throw new UnauthorizedError(parsed);
      if (response.status === 404) throw new NotFoundError(parsed);
      throw new ApiError(response.status, parsed, `HTTP ${response.status}`);
    }
    return parsed as T;
  };

  // Endpoints no-autenticados: fetch directo sin headers de auth.
  if (!authenticated) {
    return parseAndReturn(await doFetch({}));
  }

  // 1) Intentar Bearer JWT primero (AuthV2) si hay access token.
  const accessToken = getAccessToken();
  if (accessToken) {
    let response = await doFetch({ Authorization: `Bearer ${accessToken}` });

    // 2) 401 → refresh once + retry con nuevo token.
    if (response.status === 401) {
      const refreshed = await refreshAccessTokenOnce();
      const newToken = refreshed ? getAccessToken() : null;
      if (newToken) {
        response = await doFetch({ Authorization: `Bearer ${newToken}` });
      }
    }

    // 3) Si NO sigue siendo 401, devolver resultado (éxito o error real).
    if (response.status !== 401) {
      return parseAndReturn(response);
    }
    // 4) Si sigue 401 → fall-through a X-Clinician-Id legacy.
  }

  // 5) Fallback X-Clinician-Id legacy (clínicos pre-AuthV2).
  const clinicianId = getClinicianId();
  if (!clinicianId) {
    throw new UnauthorizedError({ detail: "Sin credencial local" });
  }
  return parseAndReturn(await doFetch({ "X-Clinician-Id": clinicianId }));
}

// ─── Endpoints ─────────────────────────────────────────────────

// ─── Dashboard admin: colleague feedback ──────────────────────
export type ColleagueFeedbackAnswer =
  | string
  | number
  | boolean
  | string[]
  | { choice?: string; comment?: string }
  | null;

export interface ColleagueFeedbackItem {
  id: string;
  submitted_at: string;
  full_name: string;
  email: string;
  specialty: string;
  specialty_other: string | null;
  institution: string | null;
  invitation_code: string | null;
  survey_level: SurveyLevel;
  answered_count: number;
  wants_followup: boolean;
  answers: Record<string, ColleagueFeedbackAnswer>;
  is_test: boolean;
  deleted_at: string | null;
}

export interface ColleagueFeedbackKpis {
  total: number;
  follow_up: number;
  by_specialty: Record<string, number>;
  by_level: { express: number; standard: number; deep: number };
}

export interface ColleagueFeedbackDashboard {
  kpis: ColleagueFeedbackKpis;
  items: ColleagueFeedbackItem[];
}

// Dashboard PRD v0.2 - clinician/me + preferences
export type DashboardSpecialty =
  | "cardiology"
  | "electrophysiology"
  | "internal_medicine"
  | "neurology"
  | "geriatrics"
  | "endocrinology"
  | "psychiatry"
  | "sports_medicine"
  | "anesthesiology"
  | "infectious_diseases"
  | "cardio_oncology"
  | "nephrology";

export const SPECIALTIES_DASHBOARD: readonly DashboardSpecialty[] = [
  "cardiology",
  "electrophysiology",
  "internal_medicine",
  "neurology",
  "geriatrics",
  "endocrinology",
  "psychiatry",
  "sports_medicine",
  "anesthesiology",
  "infectious_diseases",
  "cardio_oncology",
  "nephrology",
] as const;

export type PreferencesScope = "patients_list_columns";

export interface ClinicianMeOut {
  id: string;
  email: string;
  full_name: string;
  license_number: string | null;
  specialty_primary: DashboardSpecialty | null;
  specialty_secondary: DashboardSpecialty | null;
  specialty_confirmed_at: string | null;
  needs_specialty_confirmation: boolean;
  /** 'practitioner' | 'validator' | 'advisor'. Opcional — backend lo agrega en v0.2. */
  role?: string;
}

export interface SpecialtyUpdatePayload {
  specialty_primary: DashboardSpecialty;
  specialty_secondary: DashboardSpecialty | null;
}

export interface PreferencesActiveColumn {
  key: string;
  order: number;
}

export interface PreferencesPayload {
  version: number;
  active_columns: PreferencesActiveColumn[];
}

export interface PreferencesOut {
  scope: PreferencesScope;
  preferences_json: PreferencesPayload;
  source: "user" | "preset";
  preset_specialty: DashboardSpecialty | null;
  preset_version: number | null;
  preset_approved_by: string | null;
  preset_approved_at: string | null;
  updated_at: string | null;
}

export interface PreferencesUpdatePayload {
  scope: PreferencesScope;
  preferences_json: PreferencesPayload;
}

// ─── Patient Invite Tokens (PRD v0.1 S1) — tipos ───────────────
// Espejo de schemas/clinician.py::ClinicianInviteTokenRead.
export type InviteTokenState = "available" | "used" | "revoked" | "expired";

export interface ClinicianInviteTokenRead {
  id: string;
  token: string;
  state: InviteTokenState;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  used_count: number;
  used_by_patient_account_id: string | null;
}

export const api = {
  listColleagueFeedback: (opts?: { includeTest?: boolean; includeDeleted?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.includeTest) params.set("include_test", "true");
    if (opts?.includeDeleted) params.set("include_deleted", "true");
    const qs = params.toString();
    const path = qs
      ? `/api/v1/admin/colleague-feedback?${qs}`
      : "/api/v1/admin/colleague-feedback";
    return request<ColleagueFeedbackDashboard>(path);
  },

  markFeedbackAsTest: (responseId: string) =>
    request<{ id: string; is_test: boolean }>(
      `/api/v1/admin/colleague-feedback/${responseId}/mark-test`,
      { method: "PATCH" },
    ),

  softDeleteFeedback: (responseId: string) =>
    request<{ id: string; deleted_at: string | null }>(
      `/api/v1/admin/colleague-feedback/${responseId}`,
      { method: "DELETE" },
    ),

  health: () =>
    request<HealthResponse>("/api/v1/healthz", { authenticated: false }),

  // Dashboard PRD v0.2 - clinician/me + preferences
  getClinicianMe: () =>
    request<ClinicianMeOut>("/api/v1/clinician/me"),

  updateClinicianSpecialty: (payload: SpecialtyUpdatePayload) =>
    request<ClinicianMeOut>("/api/v1/clinician/me/specialty", {
      method: "PUT",
      body: payload,
    }),

  getClinicianPreferences: (scope: PreferencesScope = "patients_list_columns") =>
    request<PreferencesOut>(
      `/api/v1/clinician/me/preferences?scope=${scope}`,
    ),

  updateClinicianPreferences: (payload: PreferencesUpdatePayload) =>
    request<PreferencesOut>("/api/v1/clinician/me/preferences", {
      method: "PUT",
      body: payload,
    }),

  resetClinicianPreferences: (scope: PreferencesScope = "patients_list_columns") =>
    request<PreferencesOut>("/api/v1/clinician/me/preferences/reset", {
      method: "POST",
      body: { scope },
    }),

  references: () =>
    request<ClinicalReferencesResponse>("/api/v1/clinical/references", {
      authenticated: false,
    }),

  submitWaitlist: (payload: WaitlistSignupRequest) =>
    request<WaitlistSignupResponse>("/api/v1/waitlist", {
      method: "POST",
      body: payload,
      authenticated: false,
    }),

  submitColleagueFeedback: (payload: ColleagueFeedbackRequest) =>
    request<ColleagueFeedbackSubmitResponse>("/api/v1/colleague-feedback", {
      method: "POST",
      body: payload,
      authenticated: false,
    }),

  listPatients: () => request<PatientOut[]>("/api/v1/patients"),

  // PRD Dashboard v0.3 sec 4.7 — KPIs agregados de cohorte
  getCohortKpis: () => request<CohortKpisResponse>("/api/v1/cohort/kpis"),

  // PRD Triage View v0.1 — feed priorizado según rules de la specialty del clinico.
  // Sesion 8 (VisualAids): include_drafts=true anota cada entry con
  // draft_findings_summary; el backend valida role (admin/validator/advisor).
  getTriageFeed: (opts?: { includeDrafts?: boolean }) => {
    const qs = opts?.includeDrafts ? "?include_drafts=true" : "";
    return request<TriageFeedResponse>(`/api/v1/clinician/me/triage-feed${qs}`);
  },

  // Sesion 9: dismiss endpoints (backend S7 commit 29081fc)
  createTriageDismissal: (payload: TriageDismissalCreatePayload) =>
    request<TriageDismissal>("/api/v1/clinician/me/triage-dismissals", {
      method: "POST",
      body: payload,
    }),

  /** DELETE devuelve 204 sin body; request() lo resuelve como void. */
  deleteTriageDismissal: (id: string) =>
    request<void>(`/api/v1/clinician/me/triage-dismissals/${id}`, {
      method: "DELETE",
    }),

  listTriageDismissals: (opts?: { activeOnly?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.activeOnly) params.set("active_only", "true");
    const qs = params.toString();
    const path = qs
      ? `/api/v1/clinician/me/triage-dismissals?${qs}`
      : "/api/v1/clinician/me/triage-dismissals";
    return request<TriageDismissal[]>(path);
  },

  getPatientStats: (patientId: string, windowDays?: number) =>
    request<PatientAggregateStats>(
      windowDays != null
        ? `/api/v1/patients/${patientId}?window_days=${windowDays}`
        : `/api/v1/patients/${patientId}`,
    ),

  listPatientECGs: (patientId: string) =>
    request<ECGRecordSummary[]>(`/api/v1/patients/${patientId}/ecgs`),

  getECG: (ecgId: string) => request<ECGRecordDetail>(`/api/v1/ecgs/${ecgId}`),
  getMethodology: (metricName: string) =>
    request<MethodologyPayload>(`/api/v1/methodology/${metricName}`),

  updateNotes: (ecgId: string, notes: string) =>
    request<ECGRecordDetail>(`/api/v1/ecgs/${ecgId}/notes`, {
      method: "PATCH",
      body: { notes },
    }),

  updatePatient: (patientId: string, payload: PatientUpdatePayload) =>
    request<PatientOut>(`/api/v1/patients/${patientId}`, {
      method: "PATCH",
      body: payload,
    }),

  uploadECGs: (files: File[], patientId?: string): Promise<UploadResponse> => {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    const suffix = patientId ? `?patient_id=${encodeURIComponent(patientId)}` : "";
    return request<UploadResponse>(`/api/v1/ecg/upload${suffix}`, {
      method: "POST",
      body: formData,
    });
  },

  /**
   * Descarga el PNG del preview del ECG con autenticación.
   *
   * No podemos usar `<img src="...">` directo porque el endpoint requiere
   * header `X-Clinician-Id`. Bajamos el blob y devolvemos un object URL
   * que el componente puede usar como src y revocar al desmontarse.
   */
  getEcgPreviewBlobUrl: async (ecgId: string): Promise<string> => {
    const url = `${getApiBase()}/api/v1/ecgs/${ecgId}/preview.png`;

    const doBlobFetch = async (
      authHeaders: Record<string, string>,
    ): Promise<Response> => {
      try {
        return await fetch(url, { method: "GET", headers: authHeaders });
      } catch (e) {
        throw new NetworkError(e);
      }
    };

    // 1) Bearer primary + refresh-on-401-retry-once.
    const accessToken = getAccessToken();
    let response: Response | null = null;
    if (accessToken) {
      response = await doBlobFetch({ Authorization: `Bearer ${accessToken}` });
      if (response.status === 401) {
        const refreshed = await refreshAccessTokenOnce();
        const newToken = refreshed ? getAccessToken() : null;
        if (newToken) {
          response = await doBlobFetch({ Authorization: `Bearer ${newToken}` });
        }
      }
      if (response.status === 401) {
        response = null; // fall-through a legacy
      }
    }

    // 2) Fallback X-Clinician-Id legacy.
    if (!response) {
      const clinicianId = getClinicianId();
      if (!clinicianId) throw new UnauthorizedError({ detail: "Sin credencial local" });
      response = await doBlobFetch({ "X-Clinician-Id": clinicianId });
    }

    if (response.status === 401) throw new UnauthorizedError({ detail: "No autorizado" });
    if (response.status === 404) throw new NotFoundError({ detail: "Preview no disponible" });
    if (!response.ok) throw new ApiError(response.status, null, `HTTP ${response.status}`);

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Descarga la señal reconstruida del ECG.
   * Devuelve 15360 muestras a 512 Hz cubriendo 30 segundos.
   */
  getEcgSignal: (ecgId: string) =>
    request<EcgSignalResponse>(`/api/v1/ecgs/${ecgId}/signal`),

  /**
   * Hallazgos algoritmicos DRAFT sobre el ECG (Phase B VisualAids).
   * Requiere role admin/validator/advisor — el backend devuelve 403 para
   * roles no privilegiados. Llamar solo si `isCurrentUserPrivilegedForDrafts()`.
   */
  getEcgFindings: (ecgId: string) =>
    request<ECGFindingResponse[]>(`/api/v1/ecgs/${ecgId}/findings`),

  // ─── ECG Clinical Tags v0.1 (sesion 2 frontend) ─────────────────

  listECGTags: (ecgId: string) =>
    request<ECGTagOut[]>(`/api/v1/ecgs/${ecgId}/tags`),

  getTagVocabulary: () =>
    request<TagVocabularyEntry[]>(`/api/v1/tags/vocabulary`),

  createECGTag: (ecgId: string, payload: ECGTagIn) =>
    request<ECGTagOut>(`/api/v1/ecgs/${ecgId}/tags`, {
      method: "POST",
      body: payload,
    }),

  deleteECGTag: (ecgId: string, tagId: string, payload?: ECGTagDelete) =>
    request<ECGTagOut>(`/api/v1/ecgs/${ecgId}/tags/${tagId}`, {
      method: "DELETE",
      body: payload,
    }),

  // ─── Patient Invite Tokens (PRD v0.1 S1) ────────────────────────
  listInviteTokens: () =>
    request<ClinicianInviteTokenRead[]>("/api/v1/clinician/invite-tokens"),

  createInviteToken: () =>
    request<ClinicianInviteTokenRead>("/api/v1/clinician/invite-tokens", {
      method: "POST",
    }),

  revokeInviteToken: (tokenId: string) =>
    request<ClinicianInviteTokenRead>(
      `/api/v1/clinician/invite-tokens/${tokenId}/revoke`,
      { method: "POST" },
    ),

  listPatientExternalECGs: (patientId: string, modality?: string) =>
    request<ExternalECGOut[]>(
      `/api/v1/patients/${patientId}/external-ecgs${modality ? `?modality=${modality}` : ""}`,
    ),

  generateTrilateralInsights: (patientId: string, watchSignedReportId?: string) =>
    request<{ insights: string; prompt_version: string; model: string; cost_usd: number }>(
      `/api/v1/patients/${patientId}/trilateral-insights`,
      {
        method: "POST",
        body: { watch_signed_report_id: watchSignedReportId ?? null },
      },
    ),
};

// ─── External Clinical ECGs (12-lead anchor, Holter, etc.) ──────

export interface ExternalECGOut {
  id: string;
  patient_id: string;
  study_identifier: string | null;
  modality: string;
  recording_date: string;  // ISO date
  recording_time_local: string | null;
  device_brand: string | null;
  device_model: string | null;
  n_leads: number | null;
  hr_mean_bpm: number | null;
  pr_ms: number | null;
  qrs_ms: number | null;
  qt_ms: number | null;
  qtc_bazett_ms: number | null;
  qtc_fridericia_ms: number | null;
  axis_p_degrees: number | null;
  axis_qrs_degrees: number | null;
  axis_t_degrees: number | null;
  rhythm: string | null;
  operator_name: string | null;
  requesting_clinician: string | null;
  ectopia_ventricular_n: number | null;
  ectopia_sv_n: number | null;
  pauses_n: number | null;
  af_events_n: number | null;
  monitoring_duration_hours: number | null;
  measurements_attested_at: string | null;
  created_at: string;
}

// ─── ECG Clinical Tags v0.1 — tipos ────────────────────────────
// Espejo de los Pydantic schemas del backend (api/ecg_tags.py).

export type ECGTagType = "polarity_lead_i_inverted";

export type ECGTagCategory =
  | "signal_quality"
  | "technical_context"
  | "clinical_finding";

export interface ECGTagOut {
  id: string;
  ecg_record_id: string;
  tag_type: ECGTagType;
  category: ECGTagCategory;
  source: string;
  clinician_id: string;
  notes: string | null;
  created_at: string;
  deleted_at: string | null;
  display_es: string;
}

export interface ECGTagIn {
  tag_type: ECGTagType;
  notes?: string | null;
}

export interface ECGTagDelete {
  deletion_reason?: string | null;
}

export interface TagVocabularyEntry {
  tag_type: ECGTagType;
  category: ECGTagCategory;
  display_es: string;
  description_es: string;
  color_token: string;
}

// ─────────────────────────────────────────────────────────────────
// Cohort endpoints — PRD Dashboard Personalizable v0.3
// ─────────────────────────────────────────────────────────────────

/** Sec 4.7 — 4 contadores agregados de la cohorte del clínico. */
export interface CohortKpisResponse {
  /** Pacientes con alertas activas medium/high. Phase J: siempre 0. */
  kpi_alerts: number;
  /** Pacientes con days_since_last_ecg > 30 (excluye sin ECGs). */
  kpi_overdue: number;
  /** ECGs subidos en últimos 7 días en toda la cohorte. */
  kpi_ecgs_week: number;
  /** Pacientes con prescripción activa cuyo próximo slot cae hoy. */
  kpi_due_today: number;
}

// ─── Triage (PRD Triage View v0.1) ────────────────────────────

export interface TriageMatchedRule {
  rule_id: string;
  title: string;
  reason: string;
}

/**
 * Resumen de hallazgos algoritmicos DRAFT del ultimo ECG del paciente.
 * Solo presente en el response cuando el request paso `include_drafts=true`
 * y el clinician tiene role privilegiado (admin/validator/advisor).
 * Phase B · Sesion 8.
 */
export interface DraftFindingsSummary {
  has_drafts: boolean;
  /** v0.1: solo `premature_beats`. Extensible para FA / taq / TSV. */
  counts: Record<string, number>;
}

export interface TriagePriorityEntry {
  patient_id: string;
  patient_name: string;
  severity: "high" | "medium" | "low";
  matched_rules: TriageMatchedRule[];
  draft_findings_summary?: DraftFindingsSummary | null;
}

export interface TriageRoutineEntry {
  patient_id: string;
  patient_name: string;
  days_since_last_ecg: number | null;
  ecgs_30d: number;
  last_ecg_classification: string | null;
  draft_findings_summary?: DraftFindingsSummary | null;
}

export interface TriageFeedResponse {
  priorities: TriagePriorityEntry[];
  routine: TriageRoutineEntry[];
  phases_in_production: string[];
  rules_active_count: number;
  rules_disabled_by_phase: number;
  evaluation_errors_count: number;
}

// ─── Triage dismissals (Sesion 9, backend ya en S7 commit 29081fc) ──

export type DismissalType = "attended" | "snoozed";

export interface TriageDismissal {
  id: string;
  patient_id: string;
  /** null = dismiss aplica a TODAS las rules activas del paciente. */
  rule_id: string | null;
  dismissal_type: DismissalType;
  /** ISO datetime. Solo seteado si dismissal_type === "snoozed". */
  snooze_until: string | null;
  dismissed_at: string;
  dismissed_note: string | null;
}

export interface TriageDismissalCreatePayload {
  patient_id: string;
  /** Opcional: null = dismiss para todas las rules activas del paciente. */
  rule_id?: string | null;
  dismissal_type: DismissalType;
  /** Requerido si dismissal_type === "snoozed". ISO datetime, max +90 dias. */
  snooze_until?: string | null;
  dismissed_note?: string | null;
}


// ─── Admin · Operational telemetry ─────────────────────────────
// Endpoint: GET /api/v1/admin/latency-stats?window_days=N
// Auth: Bearer JWT (AuthV2) preferido, X-Clinician-Id legacy como fallback
// (unificado con el resto de api.ts — backlog P0 #1, auditoria 2026-09-05).
// Referencia: ECGWatch_PRD_PhaseF_LatencyHardening_v0_2.md §10.4

export interface LatencyStatsResponse {
  window_days: number;
  total_uploads: number;
  telemetry_uploads: number;
  t1_seconds_p50: number | null;
  t1_seconds_p95: number | null;
  t1_seconds_p99: number | null;
  t_total_seconds_p50: number | null;
  t_total_seconds_p95: number | null;
  t_total_seconds_p99: number | null;
}

export async function getLatencyStats(
  windowDays = 1,
): Promise<LatencyStatsResponse> {
  return request<LatencyStatsResponse>(
    `/api/v1/admin/latency-stats?window_days=${windowDays}`,
  );
}



// ─── Admin · Wakes (row-level telemetry) ──────────────────────
// Endpoint: GET /api/v1/admin/wakes?window_days=N&limit=M&offset=K
// Auth: Bearer JWT (AuthV2) preferido, X-Clinician-Id legacy como fallback
// (unificado con el resto de api.ts — backlog P0 #1, auditoria 2026-09-05).
// Backend ref: commit 6cea620 — Phase F · S6 (latency hardening data plumbing)

export interface AdminWake {
  ecg_record_id: string;
  sample_recorded_at: string | null;
  sample_observed_at: string | null;
  upload_started_at: string | null;
  received_at: string | null;
  t1_seconds: number | null;
  t_total_seconds: number | null;
  battery_level_at_wake: number | null;
  battery_state_at_wake: string | null;
  low_power_mode_at_wake: boolean | null;
  thermal_state_at_wake: string | null;
  bg_refresh_status_at_wake: string | null;
  network_type_at_wake: string | null;
  time_since_last_foreground_s: number | null;
  local_hour_at_wake: number | null;
}

export interface AdminWakesResponse {
  window_days: number;
  total: number;
  wakes: AdminWake[];
}

export async function getAdminWakes(
  windowDays = 1,
  limit = 20,
  offset = 0,
): Promise<AdminWakesResponse> {
  return request<AdminWakesResponse>(
    `/api/v1/admin/wakes?window_days=${windowDays}&limit=${limit}&offset=${offset}`,
  );
}
